import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Navbar } from '@/components/Navbar';
import { Footer } from '@/components/Footer';
import { toast } from 'sonner';
import { API, useAuth } from '@/App';
import { Calendar, DollarSign, Car as CarIcon, Clock } from 'lucide-react';
import { AddReviewForm } from '@/components/AddReviewForm';

const DashboardPage = () => {
  const { user } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [cars, setCars] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBookings();
  }, []);

  const fetchBookings = async () => {
    try {
      const bookingsResponse = await axios.get(`${API}/bookings`);
      setBookings(bookingsResponse.data);

      // Fetch car details for each booking
      const carIds = [...new Set(bookingsResponse.data.map(b => b.car_id))];
      const carPromises = carIds.map(id => axios.get(`${API}/cars/${id}`));
      const carResponses = await Promise.all(carPromises);
      
      const carsMap = {};
      carResponses.forEach(response => {
        carsMap[response.data.id] = response.data;
      });
      setCars(carsMap);
    } catch (error) {
      toast.error('Failed to load bookings');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p>Loading your bookings...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk' }} data-testid="dashboard-title">
            My Dashboard
          </h1>
          <p className="text-lg text-muted-foreground">Welcome back, {user?.name}!</p>
        </div>

        {/* Stats */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="glass-card p-6 rounded-lg" data-testid="stat-total-bookings">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Bookings</p>
                <p className="text-3xl font-bold">{bookings.length}</p>
              </div>
              <Calendar className="w-10 h-10 text-accent" />
            </div>
          </div>

          <div className="glass-card p-6 rounded-lg" data-testid="stat-active-bookings">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Active Bookings</p>
                <p className="text-3xl font-bold">
                  {bookings.filter(b => b.status === 'confirmed').length}
                </p>
              </div>
              <CarIcon className="w-10 h-10 text-accent" />
            </div>
          </div>

          <div className="glass-card p-6 rounded-lg" data-testid="stat-total-spent">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Spent</p>
                <p className="text-3xl font-bold">
                  Rs. {bookings.filter(b => b.payment_status === 'paid').reduce((sum, b) => sum + b.total_price, 0).toLocaleString()}
                </p>
              </div>
              <DollarSign className="w-10 h-10 text-accent" />
            </div>
          </div>
        </div>

        {/* Add Review Form */}
        <div className="mb-12">
          <AddReviewForm />
        </div>

        {/* Bookings List */}
        <div>
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Cabinet Grotesk' }}>My Bookings</h2>
          
          {bookings.length === 0 ? (
            <div className="text-center py-20 glass-card rounded-lg" data-testid="no-bookings-message">
              <Clock className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-xl mb-2">No bookings yet</p>
              <p className="text-muted-foreground mb-6">Start exploring our amazing fleet!</p>
              <Link to="/cars">
                <button className="btn-primary">Browse Cars</button>
              </Link>
            </div>
          ) : (
            <div className="space-y-6">
              {bookings.map((booking, index) => {
                const car = cars[booking.car_id];
                if (!car) return null;

                return (
                  <div key={booking.id} className="glass-card p-6 rounded-lg" data-testid={`booking-card-${index}`}>
                    <div className="flex flex-col md:flex-row gap-6">
                      <div className="md:w-48 h-32 rounded-lg overflow-hidden flex-shrink-0">
                        <img
                          src={car.image_url}
                          alt={car.name}
                          className="w-full h-full object-cover"
                        />
                      </div>

                      <div className="flex-1">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="text-xs uppercase tracking-wider text-accent font-mono mb-1">{car.type}</p>
                            <h3 className="text-xl font-bold" data-testid={`booking-car-name-${index}`}>{car.name}</h3>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-primary" data-testid={`booking-total-${index}`}>Rs. {booking.total_price.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">total</p>
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div>
                            <p className="text-sm text-muted-foreground">Start Date</p>
                            <p className="font-medium" data-testid={`booking-start-${index}`}>
                              {new Date(booking.start_date).toLocaleDateString()}
                            </p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">End Date</p>
                            <p className="font-medium" data-testid={`booking-end-${index}`}>
                              {new Date(booking.end_date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        <div className="flex gap-2">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`} data-testid={`booking-status-${index}`}>
                            {booking.status}
                          </span>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(booking.payment_status)}`} data-testid={`booking-payment-${index}`}>
                            Payment: {booking.payment_status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default DashboardPage;
